"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AgeVerificationsUploadRequest = exports.AgeVerificationRecord = exports.ShiftEventsUploadRequest = exports.ShiftEventUpload = exports.CashCountPayload = exports.CashMovementPayload = exports.ShiftClosePayload = exports.FiscalZSnapshot = exports.ShiftTransitionPayload = exports.ShiftOpenPayload = exports.ReconcileRequest = exports.ShiftDetail = exports.CashCountRecord = exports.CashCountDenomination = exports.ShiftReconciliationAdjustment = exports.CashMovementRecord = exports.ShiftSummary = exports.ReconcileAction = exports.CASH_MOVEMENT_DIRECTION = exports.CashMovementKind = exports.ShiftState = void 0;
exports.sameFiscalZSnapshot = sameFiscalZSnapshot;
exports.cashCountArithmeticError = cashCountArithmeticError;
const zod_1 = require("zod");
const pricing_js_1 = require("./pricing.js");
/**
 * Shift + cash-movement + reconciliation contracts (Sprint 5).
 *
 * The terminal authors shifts (open / state-transition / close) and
 * cash movements (drops / pickups / etc.) locally, then uploads them
 * to BOS via `/sync/shift-events`. BOS keeps the authoritative
 * ledger and surfaces reconciliation actions to managers.
 *
 * See `apps/bos/docs/modules/shifts.md` for the full spec, including
 * the TRS §18.2 state machine.
 */
// ---------- Primitive shapes ----------
exports.ShiftState = zod_1.z.enum([
    'open',
    'active',
    'paused',
    'closing',
    'closed',
    'reconciled',
    'abnormal_close',
]);
exports.CashMovementKind = zod_1.z.enum([
    'drop',
    'pickup',
    'bank_deposit',
    'change_order',
    // The five below are what the till actually records. They are NOT synonyms
    // of the four above: mapping `paid_in` onto `change_order` would classify
    // generic cash-in as a denomination swap, and `change_order` is specifically
    // the change float, which the till tracks as its own thing.
    'paid_in',
    'paid_out',
    'change_in',
    'change_out',
    'revenue_out',
    // 'inter_till_transfer' deferred to Sprint 7+ (multi-cashier modes B/C)
]);
/**
 * Which way the drawer's cash moves, per TRS §18.5: drop and pickup take cash
 * OUT of the drawer. A pickup is a supervisor/cash-in-transit collection;
 * float top-ups remain change_order, paid_in or change_in.
 *
 * Declared as a total `Record` over the union on purpose. Every report that
 * sums cash movements has to answer this question, and answering it inline
 * with a `filter` means a kind added later is counted by nobody and shows up
 * as a quiet under-report rather than a failure. Typed this way, the next
 * member added to the enum breaks the build here until someone states its
 * direction.
 *
 * Not a zod schema: the codegen only emits JSON Schema for `ZodType` exports,
 * so this stays a TypeScript-side fact and the wire surface is unchanged.
 */
exports.CASH_MOVEMENT_DIRECTION = {
    drop: 'out',
    pickup: 'out',
    bank_deposit: 'out',
    change_order: 'in',
    paid_in: 'in',
    paid_out: 'out',
    change_in: 'in',
    change_out: 'out',
    revenue_out: 'out',
};
exports.ReconcileAction = zod_1.z.enum(['accept', 'adjust']);
// ---------- Admin REST surface ----------
exports.ShiftSummary = zod_1.z.object({
    id: zod_1.z.string(),
    clientUuid: zod_1.z.string(),
    cashierId: zod_1.z.string(),
    terminalId: zod_1.z.string(),
    state: exports.ShiftState,
    openedAt: zod_1.z.string().datetime(),
    openingCashMinor: pricing_js_1.MoneyMinor,
    closedAt: zod_1.z.string().datetime().nullable(),
    closingCashCountedMinor: pricing_js_1.MoneyMinor.nullable(),
    closingCashExpectedMinor: pricing_js_1.MoneyMinor.nullable(),
    varianceMinor: pricing_js_1.MoneyMinor.nullable(),
    varianceReasonCode: zod_1.z.string().nullable(),
    zReportRef: zod_1.z.string().nullable(),
    reconciledAt: zod_1.z.string().datetime().nullable(),
    createdAt: zod_1.z.string().datetime(),
    updatedAt: zod_1.z.string().datetime(),
});
exports.CashMovementRecord = zod_1.z.object({
    id: zod_1.z.string(),
    clientUuid: zod_1.z.string(),
    shiftId: zod_1.z.string(),
    terminalId: zod_1.z.string(),
    kind: exports.CashMovementKind,
    amountMinor: pricing_js_1.MoneyMinor,
    reasonCode: zod_1.z.string().min(1).max(64),
    notes: zod_1.z.string().max(1000).nullable(),
    actorCashierId: zod_1.z.string().nullable(),
    supervisorApproverId: zod_1.z.string().nullable(),
    pairedMovementId: zod_1.z.string().nullable(),
    occurredAt: zod_1.z.string().datetime(),
    createdAt: zod_1.z.string().datetime(),
});
exports.ShiftReconciliationAdjustment = zod_1.z.object({
    id: zod_1.z.string(),
    shiftId: zod_1.z.string(),
    amountMinor: pricing_js_1.MoneyMinor,
    reason: zod_1.z.string().min(1).max(1000),
    actorUserId: zod_1.z.string().nullable(),
    createdAt: zod_1.z.string().datetime(),
});
/**
 * One denomination line of a drawer count: the face value in minor units and
 * how many notes or coins of it were counted.
 */
exports.CashCountDenomination = zod_1.z.object({
    // The digit re-test is not redundant: a failed `.regex()` leaves zod dirty,
    // not aborted, so this still runs on a value like 'abc', and an unguarded
    // BigInt() would throw out of safeParse (as PositiveMoneyMinor in sync.ts).
    denominationMinor: pricing_js_1.MoneyMinor.refine((v) => /^-?\d+$/.test(v) && BigInt(v) > 0n, 'denomination must be positive'),
    count: zod_1.z.number().int().min(0).max(1_000_000),
});
/**
 * A drawer count filed against a shift (VL-250). Not a movement: it asserts
 * what was physically in the drawer at `countedAt`, next to what the till
 * expected, so a Z can be reconciled against it.
 */
exports.CashCountRecord = zod_1.z.object({
    id: zod_1.z.string(),
    clientUuid: zod_1.z.string(),
    shiftId: zod_1.z.string(),
    terminalId: zod_1.z.string(),
    currency: pricing_js_1.CurrencyCode,
    expectedMinor: pricing_js_1.MoneyMinor,
    countedMinor: pricing_js_1.MoneyMinor,
    /** `countedMinor - expectedMinor`: negative is a shortage. */
    differenceMinor: pricing_js_1.MoneyMinor,
    denominations: zod_1.z.array(exports.CashCountDenomination),
    countedByCashierId: zod_1.z.string().nullable(),
    countedAt: zod_1.z.string().datetime(),
    createdAt: zod_1.z.string().datetime(),
});
exports.ShiftDetail = exports.ShiftSummary.extend({
    cashMovements: zod_1.z.array(exports.CashMovementRecord),
    reconciliationAdjustments: zod_1.z.array(exports.ShiftReconciliationAdjustment),
    /** Drawer counts filed during the shift, oldest first (VL-250). */
    cashCounts: zod_1.z.array(exports.CashCountRecord),
});
/**
 * POST /shifts/:id/reconcile body. The `adjustmentMinor` field is
 * required when `expectedAction === 'adjust'`, MUST be omitted when
 * `'accept'`. Cross-field invariant enforced via superRefine.
 */
exports.ReconcileRequest = zod_1.z
    .object({
    expectedAction: exports.ReconcileAction,
    adjustmentMinor: pricing_js_1.MoneyMinor.optional(),
    reason: zod_1.z.string().min(1).max(1000),
})
    .superRefine((req, ctx) => {
    if (req.expectedAction === 'adjust' && req.adjustmentMinor === undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['adjustmentMinor'],
            message: "adjustmentMinor required when expectedAction='adjust'",
        });
    }
    if (req.expectedAction === 'accept' && req.adjustmentMinor !== undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['adjustmentMinor'],
            message: "adjustmentMinor must be omitted when expectedAction='accept'",
        });
    }
});
// ---------- Sync upload — `/sync/shift-events` (terminal → BOS) ----------
/**
 * Discriminated union of event kinds the terminal uploads. The
 * `payload` shape varies by `kind`; BOS dispatches into the right
 * persistence path. `clientUuid` is the idempotency key — replay
 * is safe per the existing H-86 / H-87 pattern.
 */
exports.ShiftOpenPayload = zod_1.z.object({
    cashierId: zod_1.z.string(),
    terminalId: zod_1.z.string(),
    openingCashMinor: pricing_js_1.MoneyMinor,
    openingNotes: zod_1.z.string().max(1000).nullable(),
    /**
     * The fiscal register's own number for the shift being opened — what it
     * prints on every receipt of the shift and on the Z that closes it
     * (YUKI-228). Optional: only a register that numbers shifts sends it, and a
     * till that could not ask its register leaves it out.
     */
    shiftNumber: zod_1.z.string().trim().min(1).max(64).optional(),
});
exports.ShiftTransitionPayload = zod_1.z.object({
    shiftClientUuid: zod_1.z.string(),
    from: exports.ShiftState,
    to: exports.ShiftState,
});
/** Immutable successful RRO Z turnovers. Slots are RRO tax-table positions. */
exports.FiscalZSnapshot = zod_1.z
    .object({
    zNumber: zod_1.z.string().regex(/^[1-9][0-9]*$/),
    registrarId: zod_1.z.string().trim().min(1).max(255),
    currency: zod_1.z.literal('UAH'),
    sums: zod_1.z.array(zod_1.z.string().regex(/^[0-9]+\.[0-9]{2}$/)).length(9),
    returnSums: zod_1.z.array(zod_1.z.string().regex(/^[0-9]+\.[0-9]{2}$/)).length(9),
    coverage: zod_1.z
        .object({
        saleCount: zod_1.z.number().int().nonnegative(),
        refundCount: zod_1.z.number().int().nonnegative(),
        saleTotal: zod_1.z.string().regex(/^[0-9]+\.[0-9]{2}$/),
        refundTotal: zod_1.z.string().regex(/^[0-9]+\.[0-9]{2}$/),
        saleCash: zod_1.z.string().regex(/^[0-9]+\.[0-9]{2}$/),
        refundCash: zod_1.z.string().regex(/^[0-9]+\.[0-9]{2}$/),
        cashIn: zod_1.z.string().regex(/^[0-9]+\.[0-9]{2}$/),
        cashOut: zod_1.z.string().regex(/^[0-9]+\.[0-9]{2}$/),
    })
        .strict()
        .optional(),
})
    .strict();
/**
 * Whether two stored/uploaded fiscal Z snapshots are the same evidence.
 *
 * Key order is ignored (PostgreSQL `jsonb` reorders keys); everything else —
 * every slot string, count and identity field — must match exactly. Hosts use
 * it to tell an idempotent re-sent close from a conflicting second claim for
 * a shift whose first evidence is already kept.
 */
function sameFiscalZSnapshot(a, b) {
    const canonical = (value) => {
        if (Array.isArray(value))
            return value.map(canonical);
        if (value !== null && typeof value === 'object') {
            const record = value;
            return Object.keys(record)
                .sort()
                .map((key) => [key, canonical(record[key])]);
        }
        return value;
    };
    return JSON.stringify(canonical(a)) === JSON.stringify(canonical(b));
}
exports.ShiftClosePayload = zod_1.z.object({
    shiftClientUuid: zod_1.z.string(),
    closingCashCountedMinor: pricing_js_1.MoneyMinor,
    closingCashExpectedMinor: pricing_js_1.MoneyMinor,
    varianceMinor: pricing_js_1.MoneyMinor,
    varianceReasonCode: zod_1.z.string().max(64).nullable(),
    varianceNotes: zod_1.z.string().max(1000).nullable(),
    zReportRef: zod_1.z.string().max(255).nullable(),
    fiscalZ: exports.FiscalZSnapshot.optional(),
});
exports.CashMovementPayload = zod_1.z.object({
    shiftClientUuid: zod_1.z.string(),
    terminalId: zod_1.z.string(),
    kind: exports.CashMovementKind,
    amountMinor: pricing_js_1.MoneyMinor,
    denominationBreakdown: zod_1.z.record(zod_1.z.string(), zod_1.z.number().int().min(0)).nullable(),
    reasonCode: zod_1.z.string().min(1).max(64),
    notes: zod_1.z.string().max(1000).nullable(),
    actorCashierId: zod_1.z.string().nullable(),
    supervisorApproverId: zod_1.z.string().nullable(),
    occurredAt: zod_1.z.string().datetime(),
});
/**
 * `cash-count` (VL-250): the cashier counted the drawer by denomination.
 *
 * The arithmetic travels with the record (counted, expected, difference) so
 * Back Office stores what the cashier was shown, and it is re-checked on
 * ingest: `countedMinor` must be the sum of the denomination lines and
 * `differenceMinor` must be `countedMinor - expectedMinor`, or the record is
 * refused rather than stored with figures that disagree with each other.
 * `countedByCashierId` is the till's cashier id; it is kept as given and not
 * resolved against the cashier directory, because on a store whose cashiers
 * come from elsewhere Back Office may not know it.
 */
exports.CashCountPayload = zod_1.z.object({
    shiftClientUuid: zod_1.z.string(),
    terminalId: zod_1.z.string(),
    currency: pricing_js_1.CurrencyCode,
    expectedMinor: pricing_js_1.MoneyMinor,
    countedMinor: pricing_js_1.MoneyMinor,
    differenceMinor: pricing_js_1.MoneyMinor,
    denominations: zod_1.z.array(exports.CashCountDenomination).max(64),
    countedByCashierId: zod_1.z.string().max(64).nullable(),
    countedAt: zod_1.z.string().datetime(),
});
/**
 * Why a cash count's figures disagree with each other, or null when they add
 * up. Shared so the ingest and any other reader check the same rule.
 */
function cashCountArithmeticError(payload) {
    const seen = new Set();
    let sum = 0n;
    for (const line of payload.denominations) {
        const key = BigInt(line.denominationMinor).toString();
        if (seen.has(key))
            return `denomination ${key} is listed twice`;
        seen.add(key);
        sum += BigInt(line.denominationMinor) * BigInt(line.count);
    }
    const counted = BigInt(payload.countedMinor);
    if (sum !== counted) {
        return `countedMinor ${counted} is not the sum of the denominations (${sum})`;
    }
    const expected = BigInt(payload.expectedMinor);
    if (counted - expected !== BigInt(payload.differenceMinor)) {
        return `differenceMinor ${payload.differenceMinor} is not countedMinor - expectedMinor (${counted - expected})`;
    }
    return null;
}
exports.ShiftEventUpload = zod_1.z.discriminatedUnion('kind', [
    zod_1.z.object({
        kind: zod_1.z.literal('shift.open'),
        clientUuid: zod_1.z.string().min(1).max(64),
        occurredAt: zod_1.z.string().datetime(),
        payload: exports.ShiftOpenPayload,
    }),
    zod_1.z.object({
        kind: zod_1.z.literal('shift.transition'),
        clientUuid: zod_1.z.string().min(1).max(64),
        occurredAt: zod_1.z.string().datetime(),
        payload: exports.ShiftTransitionPayload,
    }),
    zod_1.z.object({
        kind: zod_1.z.literal('shift.close'),
        clientUuid: zod_1.z.string().min(1).max(64),
        occurredAt: zod_1.z.string().datetime(),
        payload: exports.ShiftClosePayload,
    }),
    zod_1.z.object({
        kind: zod_1.z.literal('cash-movement'),
        clientUuid: zod_1.z.string().min(1).max(64),
        occurredAt: zod_1.z.string().datetime(),
        payload: exports.CashMovementPayload,
    }),
    zod_1.z.object({
        kind: zod_1.z.literal('cash-count'),
        clientUuid: zod_1.z.string().min(1).max(64),
        occurredAt: zod_1.z.string().datetime(),
        payload: exports.CashCountPayload,
    }),
]);
exports.ShiftEventsUploadRequest = zod_1.z.object({
    records: zod_1.z.array(exports.ShiftEventUpload).min(1).max(5000),
});
// ---------- Sync upload — `/sync/age-verifications` (terminal → BOS) ----------
/**
 * Cashier-attested age verification per TRS §5.7. Recorded as a
 * ledger; no further reporting in Sprint 5 (filed for Sprint 12).
 */
exports.AgeVerificationRecord = zod_1.z.object({
    clientUuid: zod_1.z.string().min(1).max(64),
    /**
     * Terminal-side ULID of the open shift the verification was rung
     * under. BOS resolves to `shifts.id` via the
     * `(terminalId, clientUuid)` UNIQUE before insert — same pattern
     * as SyncTransactionRecord.shiftClientUuid + CashMovementPayload.
     * Pre-rename (≤ commit d2d3d59) this field was named `shiftId`
     * and was treated as the BOS server cuid (H-178).
     */
    shiftClientUuid: zod_1.z.string(),
    actorCashierId: zod_1.z.string(),
    attestedAt: zod_1.z.string().datetime(),
    ageRestriction: zod_1.z.number().int().min(1).max(150),
    itemIds: zod_1.z.array(zod_1.z.string()).min(1).max(100),
});
exports.AgeVerificationsUploadRequest = zod_1.z.object({
    records: zod_1.z.array(exports.AgeVerificationRecord).min(1).max(5000),
});
//# sourceMappingURL=shifts.js.map